import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@wovendays/i18n';
import { apiClient } from '@/lib/api-client';
import { Tile } from '@/components/ui/tile';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

const TOTAL_STEPS = 4;

interface CreatedFamily {
  id: string;
  name: string;
  ownerId: string;
  createdAt: number;
}

interface CreatedInvite {
  id: string;
  token: string;
  familyId: string;
  familyName: string;
  defaultRole: string;
  expiresAt: number;
  inviteUrl: string;
}

export function OnboardingWizard() {
  const [step, setStep] = useState(1);
  const [family, setFamily] = useState<CreatedFamily | null>(null);
  const navigate = useNavigate();

  function handleFamilyCreated(createdFamily: CreatedFamily) {
    setFamily(createdFamily);
    setStep(2);
  }

  function handleNext() {
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
    } else {
      if (family) {
        navigate(`/families/${family.id}/timeline`);
      } else {
        navigate('/');
      }
    }
  }

  function handleFinish() {
    if (family) {
      navigate(`/families/${family.id}/timeline`);
    } else {
      navigate('/');
    }
  }

  return (
    <div className="mx-auto max-w-lg">
      <ProgressBar currentStep={step} totalSteps={TOTAL_STEPS} />

      {step === 1 && (
        <StepCreateFamily onCreated={handleFamilyCreated} />
      )}
      {step === 2 && family && (
        <StepAddChildren familyId={family.id} onNext={handleNext} />
      )}
      {step === 3 && family && (
        <StepInviteMembers familyId={family.id} onNext={handleNext} />
      )}
      {step === 4 && family && (
        <StepUploadPhotos onFinish={handleFinish} />
      )}
    </div>
  );
}

function ProgressBar({ currentStep, totalSteps }: { currentStep: number; totalSteps: number }) {
  const percentage = (currentStep / totalSteps) * 100;
  const { t } = useTranslation('onboarding');

  return (
    <div className="mb-6" role="progressbar" aria-valuenow={currentStep} aria-valuemin={1} aria-valuemax={totalSteps} aria-label="Onboarding progress">
      <div className="flex items-center justify-between mb-2">
        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
          {t('stepOf', { current: currentStep, total: totalSteps })}
        </p>
      </div>
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full bg-primary-600 transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function StepCreateFamily({ onCreated }: { onCreated: (family: CreatedFamily) => void }) {
  const { t } = useTranslation('onboarding');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (familyName: string) =>
      apiClient.post<{ family: CreatedFamily }>('/api/families', { name: familyName }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['families'] });
      onCreated(data.family);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : t('createFamily.failed'));
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setValidationError(null);

    if (!name.trim()) {
      setValidationError(t('createFamily.nameRequired'));
      return;
    }

    mutation.mutate(name.trim());
  }

  return (
    <Tile variant="white" className="p-8">
      <h2 className="font-serif text-2xl text-foreground mb-2">{t('createFamily.title')}</h2>
      <p className="text-muted-foreground mb-6">
        {t('createFamily.description')}
      </p>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="onboarding-family-name" className="block font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
            {t('createFamily.nameLabel')}
          </label>
          <Input
            id="onboarding-family-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('createFamily.namePlaceholder')}
            autoFocus
          />
          {validationError && (
            <p className="mt-1.5 text-sm text-destructive">{validationError}</p>
          )}
        </div>
        <Button type="submit" disabled={mutation.isPending} className="w-full">
          {mutation.isPending ? t('createFamily.creating') : t('createFamily.createFamily')}
        </Button>
      </form>
    </Tile>
  );
}

function StepAddChildren({ familyId, onNext }: { familyId: string; onNext: () => void }) {
  const { t } = useTranslation('onboarding');
  const [children, setChildren] = useState<{ name: string; birthDate: string }[]>([]);
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const addChildMutation = useMutation({
    mutationFn: (data: { name: string; birthDate: number }) =>
      apiClient.post<{ child: { id: string; name: string } }>(`/api/families/${familyId}/children`, data),
    onSuccess: (_data, variables) => {
      setChildren([...children, { name: variables.name, birthDate: new Date(variables.birthDate).toISOString().split('T')[0]! }]);
      setName('');
      setBirthDate('');
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['children', familyId] });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : t('addChildren.failed'));
    },
  });

  function handleAddChild(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !birthDate) return;
    addChildMutation.mutate({
      name: name.trim(),
      birthDate: new Date(birthDate).getTime(),
    });
  }

  return (
    <Tile variant="white" className="p-8">
      <h2 className="font-serif text-2xl text-foreground mb-2">{t('addChildren.title')}</h2>
      <p className="text-muted-foreground mb-6">
        {t('addChildren.description')}
      </p>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* List of added children */}
      {children.length > 0 && (
        <div className="mb-4 space-y-2">
          {children.map((child, i) => (
            <div key={i} className="flex items-center gap-2 rounded-xl bg-[#faf8f5] dark:bg-zinc-800/50 px-3 py-2">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-500 text-white text-xs font-semibold">
                {child.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">{child.name}</p>
                <p className="font-mono text-xs text-muted-foreground">{child.birthDate}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add child form */}
      <form onSubmit={handleAddChild} className="mb-6">
        <div className="space-y-3">
          <div>
            <label htmlFor="onboarding-child-name" className="block font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
              {t('addChildren.nameLabel')}
            </label>
            <Input
              id="onboarding-child-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('addChildren.namePlaceholder')}
            />
          </div>
          <div>
            <label htmlFor="onboarding-child-birthdate" className="block font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
              {t('addChildren.birthDateLabel')}
            </label>
            <Input
              id="onboarding-child-birthdate"
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
            />
          </div>
          <Button
            type="submit"
            variant="secondary"
            disabled={addChildMutation.isPending || !name.trim() || !birthDate}
            className="w-full"
          >
            {addChildMutation.isPending ? t('addChildren.adding') : t('addChildren.addChild')}
          </Button>
        </div>
      </form>

      <div className="flex gap-2">
        <Button onClick={onNext} className="flex-1">
          {children.length > 0 ? t('common:continue') : t('common:skip')}
        </Button>
      </div>
    </Tile>
  );
}

function StepInviteMembers({ familyId, onNext }: { familyId: string; onNext: () => void }) {
  const { t } = useTranslation('onboarding');
  const [invite, setInvite] = useState<CreatedInvite | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createInviteMutation = useMutation({
    mutationFn: () =>
      apiClient.post<{ invite: CreatedInvite }>(`/api/families/${familyId}/invites`, {
        defaultRole: 'viewer',
        expiresInDays: 7,
      }),
    onSuccess: (data) => {
      setInvite(data.invite);
      setError(null);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : t('invite.failed'));
    },
  });

  async function handleCopy() {
    if (!invite) return;
    try {
      await navigator.clipboard.writeText(invite.inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for environments without clipboard API
    }
  }

  return (
    <Tile variant="white" className="p-8">
      <h2 className="font-serif text-2xl text-foreground mb-2">{t('invite.title')}</h2>
      <p className="text-muted-foreground mb-6">
        {t('invite.description')}
      </p>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {invite ? (
        <div className="mb-6">
          <p className="text-sm text-muted-foreground mb-2">{t('invite.shareLink')}</p>
          <div className="flex gap-2">
            <code className="flex-1 rounded-xl bg-[hsl(var(--input))] px-3 py-2 font-mono text-xs text-foreground break-all">
              {invite.inviteUrl}
            </code>
            <Button variant="outline" size="sm" onClick={handleCopy}>
              {copied ? t('common:copied') : t('common:copy')}
            </Button>
          </div>
          <p className="mt-2 font-mono text-xs text-muted-foreground">
            {t('invite.expires', { date: new Date(invite.expiresAt).toLocaleDateString() })}
          </p>
        </div>
      ) : (
        <div className="mb-6">
          <Button
            onClick={() => createInviteMutation.mutate()}
            disabled={createInviteMutation.isPending}
            variant="secondary"
            className="w-full"
          >
            {createInviteMutation.isPending ? t('invite.creating') : t('invite.createInviteLink')}
          </Button>
        </div>
      )}

      <Button onClick={onNext} className="w-full">
        {invite ? t('common:continue') : t('common:skip')}
      </Button>
    </Tile>
  );
}

function StepUploadPhotos({ onFinish }: { onFinish: () => void }) {
  const { t } = useTranslation('onboarding');
  return (
    <Tile variant="white" className="p-8 text-center">
      <h2 className="font-serif text-2xl text-foreground mb-2">{t('complete.title')}</h2>
      <p className="text-muted-foreground mb-6">
        {t('complete.description')}
      </p>

      <Button onClick={onFinish} className="w-full">
        {t('complete.goToTimeline')}
      </Button>
    </Tile>
  );
}
